import MDEditor from "@uiw/react-md-editor";
import { InfoIcon, XIcon } from "lucide-react";
import { useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import rehypeSanitize from "rehype-sanitize";

import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

const MAX_PERSONALIZATION_LENGTH = 5000;
const PERSONALIZATION_FIELD_ID = "chat_personalization";

const EXAMPLE_MARKDOWN = `## Industry Focus
When analyzing contracts, prioritize:
- Coverage limits and exclusions
- Premium calculation methods
- Deductible structures

## Terminology Preferences
- Use "Insurer" and "Policyholder" instead of "Party A" and "Party B"
- Reference industry-standard terms when applicable

## Business Context
Focus on commercial property insurance for small businesses in the hospitality sector.`;

const GUIDELINES = [
    "✓ Be specific about your industry terminology and priorities",
    "✓ Provide context about your organization's business focus",
    "✓ Use markdown formatting for clear structure",
    "⚠ Avoid overly restrictive instructions that prevent helpful responses",
];

interface IProps {
    isLoading?: boolean;
}

export const ChatPersonalizationEditor = ({ isLoading = false }: IProps) => {
    const { control, watch } = useFormContext();
    const chatPersonalization: string = watch("chat_personalization") || "";
    const characterCount = chatPersonalization.length;
    const [showGuidelines, setShowGuidelines] = useState(true);
    const approachingLimit = characterCount > 4500 && characterCount <= 5000;

    return (
        <div className="flex flex-col gap-2">
            <div>
                <Label htmlFor={PERSONALIZATION_FIELD_ID}>
                    Chat Personalization Instructions
                </Label>
                <p className="text-muted-foreground mt-1 text-sm">
                    Customize how the AI chat responds to questions for your
                    organization. These instructions are added to the system
                    prompt.
                </p>
            </div>

            {showGuidelines && (
                <div
                    role="note"
                    className="border-border bg-muted/40 text-foreground flex items-start gap-2 rounded-md border p-3 text-sm"
                >
                    <InfoIcon
                        aria-hidden="true"
                        className="text-muted-foreground mt-0.5 size-4 shrink-0"
                    />
                    <div className="flex flex-1 flex-col gap-1">
                        <span className="text-sm font-semibold">
                            Guidelines:
                        </span>
                        <ul className="text-muted-foreground flex flex-col gap-0.5 text-sm">
                            {GUIDELINES.map((guideline) => (
                                <li
                                    key={guideline}
                                    className="list-none leading-snug"
                                >
                                    {guideline}
                                </li>
                            ))}
                        </ul>
                    </div>
                    <button
                        type="button"
                        onClick={() => setShowGuidelines(false)}
                        aria-label="Dismiss guidelines"
                        className="text-muted-foreground hover:text-foreground hover:bg-accent inline-flex size-6 shrink-0 items-center justify-center rounded-md transition-colors"
                    >
                        <XIcon aria-hidden="true" className="size-3.5" />
                    </button>
                </div>
            )}

            <Controller
                name="chat_personalization"
                control={control}
                render={({ field }) => (
                    <div data-color-mode="light" id={PERSONALIZATION_FIELD_ID}>
                        <MDEditor
                            value={field.value || ""}
                            onChange={(value) => field.onChange(value || "")}
                            preview="edit"
                            hideToolbar={isLoading}
                            textareaProps={{
                                placeholder: EXAMPLE_MARKDOWN,
                                maxLength: MAX_PERSONALIZATION_LENGTH,
                                disabled: isLoading,
                            }}
                            previewOptions={{
                                rehypePlugins: [[rehypeSanitize]],
                            }}
                            minHeight={300}
                        />
                    </div>
                )}
            />

            <p className="text-muted-foreground text-sm">
                {characterCount}/{MAX_PERSONALIZATION_LENGTH} characters
                {approachingLimit && (
                    <span
                        className={cn(
                            "ml-2 text-xs",
                            "text-amber-600 dark:text-amber-400"
                        )}
                    >
                        (approaching limit)
                    </span>
                )}
            </p>
        </div>
    );
};
